// Comprehensive Mock Stations Data
// Based on project data with master/slave station structure
// Each project has 1 master station and multiple slave stations

import { PROJECTS_DATA } from './projectsData';
import { computeFieldSplit } from './areaOverlayUtils';

// Calculate station status based on current conditions
const calculateStationStatus = (station) => {
  // Demo baseline: all stations are operational.
  // Keep telemetry realistic while forcing an "all green" fleet.
  return 'active';
};

// Generate base station data with sensor readings
const generateStationReadings = (coords, isMaster) => {
  const baseSeed = ((coords?.[0] || 0) * 1000000) + ((coords?.[1] || 0) * 1000000) + (isMaster ? 97 : 53);
  const seeded = (offset) => {
    const value = Math.sin(baseSeed * 0.0001 + offset * 12.34567) * 43758.5453123;
    return value - Math.floor(value);
  };

  const range = (min, max, offset) => min + (max - min) * seeded(offset);

  // Realistic, deterministic telemetry ranges.
  const baseTemp = range(24.2, 31.6, 1);
  const baseHumidity = range(64.0, 93.0, 2);
  const baseWind = range(3.4, 12.4, 3);
  const baseSolar = range(410, 940, 4);

  // Generate wind direction (0-360 degrees, where 0 is North)
  const baseWindDirection = ((coords[0] * 100 + coords[1] * 100) % 360);
  const windDirectionVariation = (seeded(5) - 0.5) * 18;
  const windDirection = (baseWindDirection + windDirectionVariation + 360) % 360;

  // Master stations are typically grid-powered; some slaves are battery.
  const isGridPowered = isMaster || seeded(6) < 0.35;

  // Keep transmissions recent to enforce active status.
  const transmissionDelay = 1 + Math.floor(seeded(7) * 9);
  const lastTransmission = new Date(Date.now() - transmissionDelay * 60 * 1000);

  const signalStrength = isGridPowered
    ? Math.round(range(-69, -61, 8))
    : Math.round(range(-76, -66, 9));

  // Keep all active stations in healthy battery ranges for demo consistency.
  const batteryLevel = isGridPowered
    ? Math.round(range(90, 100, 10))
    : Math.round(range(78, 98, 11));

  const isActive = true;

  return {
    isGridPowered,
    lastTransmission,
    signalStrength,
    batteryLevel,
    isActive,
    hasWarning: false,
    status: 'active',
    currentReadings: isActive ? {
      temperature: parseFloat(baseTemp.toFixed(1)),
      humidity: parseFloat(baseHumidity.toFixed(1)),
      windSpeed: parseFloat(baseWind.toFixed(1)),
      windDirection: Math.round(windDirection),
      solarRadiation: Math.floor(baseSolar),
      timestamp: lastTransmission,
    } : undefined,
  };
};

const getStationCodeBase = (project) => {
  if (!project) return '';
  return (project.codePrefix || project.title || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9_-]/g, '');
};

const getPolygonBounds = (polygon) => {
  if (!polygon || polygon.length === 0) return null;
  const lats = polygon.map(point => point[0]);
  const lons = polygon.map(point => point[1]);
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLon: Math.min(...lons),
    maxLon: Math.max(...lons),
  };
};

const GEOMETRY_EPSILON = 1e-9;
const A3_EMILIA_STATION_LON_SHIFT = 0.00030;
const A3_EMILIA_STATION_LON_OFFSETS = {
  'a3-emilia-campo-012': A3_EMILIA_STATION_LON_SHIFT,
  'a3-emilia-campo-013': A3_EMILIA_STATION_LON_SHIFT,
  'a3-emilia-campo-015': A3_EMILIA_STATION_LON_SHIFT,
  'a3-emilia-campo-023': A3_EMILIA_STATION_LON_SHIFT,
  'a3-emilia-retro-015': -A3_EMILIA_STATION_LON_SHIFT,
  'a3-emilia-retro-017': -A3_EMILIA_STATION_LON_SHIFT,
  'a3-emilia-retro-023': -A3_EMILIA_STATION_LON_SHIFT,
};

const isPointOnSegment = (point, a, b) => {
  if (!point || !a || !b) return false;
  const [py, px] = point;
  const [ay, ax] = a;
  const [by, bx] = b;
  const cross = (px - ax) * (by - ay) - (py - ay) * (bx - ax);
  if (Math.abs(cross) > GEOMETRY_EPSILON) return false;
  const dot = (px - ax) * (bx - ax) + (py - ay) * (by - ay);
  if (dot < -GEOMETRY_EPSILON) return false;
  const lenSq = (bx - ax) * (bx - ax) + (by - ay) * (by - ay);
  return dot - lenSq <= GEOMETRY_EPSILON;
};

const isPointInPolygon = (point, polygon) => {
  if (!point || !polygon || polygon.length < 3) return false;
  const [y, x] = point; // lat, lon
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const current = polygon[i];
    const prev = polygon[j];
    if (isPointOnSegment(point, prev, current)) return true;
    const [yi, xi] = current;
    const [yj, xj] = prev;
    const intersects = (yi > y) !== (yj > y)
      && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }

  return inside;
};

const getPolygonCentroid = (polygon) => {
  if (!polygon || polygon.length < 3) return null;
  let area = 0;
  let cx = 0;
  let cy = 0;
  const count = polygon.length;

  for (let i = 0; i < count; i += 1) {
    const [y0, x0] = polygon[i];
    const [y1, x1] = polygon[(i + 1) % count];
    const cross = x0 * y1 - x1 * y0;
    area += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }

  area *= 0.5;
  if (Math.abs(area) < GEOMETRY_EPSILON) {
    const sum = polygon.reduce(
      (acc, [lat, lon]) => {
        acc.lat += lat;
        acc.lon += lon;
        return acc;
      },
      { lat: 0, lon: 0 }
    );
    return [sum.lat / polygon.length, sum.lon / polygon.length];
  }

  return [cy / (6 * area), cx / (6 * area)];
};

const findAnyPointInsidePolygon = (polygon) => {
  if (!polygon || polygon.length < 3) return null;

  const centroid = getPolygonCentroid(polygon);
  if (centroid && isPointInPolygon(centroid, polygon)) return centroid;

  const bounds = getPolygonBounds(polygon);
  if (!bounds) return centroid;

  const boundsCenter = [
    (bounds.minLat + bounds.maxLat) / 2,
    (bounds.minLon + bounds.maxLon) / 2,
  ];
  if (isPointInPolygon(boundsCenter, polygon)) return boundsCenter;

  const gridSize = 7;
  const latStep = (bounds.maxLat - bounds.minLat) / (gridSize - 1);
  const lonStep = (bounds.maxLon - bounds.minLon) / (gridSize - 1);
  const reference = centroid || boundsCenter;
  const candidates = [];

  for (let i = 0; i < gridSize; i += 1) {
    for (let j = 0; j < gridSize; j += 1) {
      const lat = bounds.minLat + latStep * i;
      const lon = bounds.minLon + lonStep * j;
      const dist2 = (lat - reference[0]) ** 2 + (lon - reference[1]) ** 2;
      candidates.push({ point: [lat, lon], dist2 });
    }
  }

  candidates.sort((a, b) => a.dist2 - b.dist2);
  for (const candidate of candidates) {
    if (isPointInPolygon(candidate.point, polygon)) return candidate.point;
  }

  return polygon[0] || boundsCenter;
};

const ensurePointInsidePolygon = (point, polygon) => {
  if (!polygon || polygon.length < 3) return point;
  if (!Array.isArray(point) || point.length < 2) return point;
  if (isPointInPolygon(point, polygon)) return point;

  const insidePoint = findAnyPointInsidePolygon(polygon);
  if (!insidePoint || !isPointInPolygon(insidePoint, polygon)) {
    return insidePoint || point;
  }

  let lo = 0;
  let hi = 1;
  let best = insidePoint;

  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    const candidate = [
      point[0] + (insidePoint[0] - point[0]) * mid,
      point[1] + (insidePoint[1] - point[1]) * mid,
    ];
    if (isPointInPolygon(candidate, polygon)) {
      best = candidate;
      hi = mid;
    } else {
      lo = mid;
    }
  }

  return best;
};

const applyA3EmiliaStationShift = (station, polygon) => {
  if (!station || !polygon || polygon.length < 3) return;
  const offset = A3_EMILIA_STATION_LON_OFFSETS[String(station.stationCode || '').toLowerCase()];
  if (!offset) return;

  const shifted = [station.locationLat, station.locationLon + offset];
  const adjusted = ensurePointInsidePolygon(shifted, polygon);
  if (Array.isArray(adjusted) && adjusted.length === 2) {
    station.locationLat = adjusted[0];
    station.locationLon = adjusted[1];
  }
};

const getSectionCenter = (section) => {
  if (section?.center && section.center.length === 2) {
    return section.center;
  }
  const bounds = getPolygonBounds(section?.polygon);
  if (!bounds) return [0, 0];
  return [(bounds.minLat + bounds.maxLat) / 2, (bounds.minLon + bounds.maxLon) / 2];
};

const getSectionPositions = (section) => {
  const center = getSectionCenter(section);
  const bounds = getPolygonBounds(section?.polygon);
  if (!bounds) {
    const fallbackOffset = 0.00008;
    return {
      campo: [center[0] - fallbackOffset, center[1] - fallbackOffset],
      retro: [center[0] + fallbackOffset, center[1] + fallbackOffset],
    };
  }

  const latSpan = bounds.maxLat - bounds.minLat;
  const lonSpan = bounds.maxLon - bounds.minLon;
  const latOffset = latSpan * 0.18;
  const lonOffset = lonSpan * 0.18;

  const positions = {
    campo: [center[0] - latOffset, center[1] - lonOffset],
    retro: [center[0] + latOffset, center[1] + lonOffset],
  };

  const polygon = section?.polygon;
  if (Array.isArray(polygon) && polygon.length >= 3) {
    return {
      campo: ensurePointInsidePolygon(positions.campo, polygon),
      retro: ensurePointInsidePolygon(positions.retro, polygon),
    };
  }

  return positions;
};

const generateSectionStations = (project) => {
  const baseCode = getStationCodeBase(project);
  const excludedStationNumbers = Array.isArray(project?.excludedStationNumbers)
    ? new Set(
      project.excludedStationNumbers
        .map(value => Number(value))
        .filter(Number.isFinite)
    )
    : null;
  const useSequentialNumbers = excludedStationNumbers && excludedStationNumbers.size > 0;

  const sectionStations = [];
  let includedSectionIndex = 0;

  project.sections.forEach((section, index) => {
    const sectionNumber = index + 1;
    if (excludedStationNumbers?.has(sectionNumber)) {
      return;
    }

    includedSectionIndex += 1;
    const positions = getSectionPositions(section);
    const blockCode = String(includedSectionIndex).padStart(3, '0');
    const sectionId = section.id || `block-${blockCode}`;
    const sectionLabel = project.id === 'project-5'
      ? `Block ${blockCode}`
      : (section.name || (useSequentialNumbers ? `Section ${blockCode}` : section.id) || `Section ${blockCode}`);

    const makeStation = (sensorType, coords) => {
      const readings = generateStationReadings(coords, false);
      const typeLabel = sensorType === 'campo_aperto' ? 'CAMPO' : 'RETRO';

      const station = {
        id: `${project.id}-${sectionId}-${sensorType}`,
        stationCode: `${baseCode}-${typeLabel}-${blockCode}`,
        farmId: project.id,
        farmName: project.title,
        projectId: project.id,
        projectTitle: project.title,
        stationType: sensorType,
        sensorType,
        isMaster: false,
        sectionId,
        locationDescription: `${sectionLabel} - ${sensorType === 'campo_aperto' ? 'Campo Aperto' : 'Retro Modulo'}`,
        locationLat: coords[0],
        locationLon: coords[1],
        description: sensorType === 'retro_modulo'
          ? 'Retro modulo sensor transmitting data via radio'
          : 'Field sensor transmitting data via radio',
        isActive: readings.isActive,
        status: readings.status,
        lastTransmission: readings.lastTransmission,
        signalStrength: readings.signalStrength,
        batteryLevel: readings.batteryLevel,
        currentReadings: readings.currentReadings,
      };
      if (project.id === 'project-5') {
        applyA3EmiliaStationShift(station, section?.polygon);
      }
      return station;
    };

    sectionStations.push(makeStation('campo_aperto', positions.campo));
    sectionStations.push(makeStation('retro_modulo', positions.retro));
  });

  return sectionStations;
};

// Generate stations for a project based on master/slave configuration
const generateProjectStations = (project) => {
  const baseCode = getStationCodeBase(project);
  if (project.sections && project.sections.length > 0) {
    return generateSectionStations(project);
  }

  const stations = [];
  let stationIndex = 0;
  
  // Generate Master Station (retro_modulo type - receives data from slaves)
  const masterCoords = project.masterStation;
  const masterReadings = generateStationReadings(masterCoords, true);
  
  stations.push({
    id: `${project.id}-master`,
    stationCode: `${baseCode}-RETRO-001`,
    farmId: project.id,
    farmName: project.title,
    projectId: project.id,
    projectTitle: project.title,
    stationType: 'retro_modulo',
    sensorType: 'retro_modulo',
    isMaster: true,
    locationDescription: `Master Station - ${project.location}`,
    locationLat: masterCoords[0],
    locationLon: masterCoords[1],
    description: 'Master station receiving data via radio and transmitting to cloud via 4G',
    isActive: masterReadings.isActive,
    status: masterReadings.status,
    lastTransmission: masterReadings.lastTransmission,
    signalStrength: masterReadings.signalStrength,
    batteryLevel: masterReadings.batteryLevel,
    currentReadings: masterReadings.currentReadings,
  });
  
  // Generate Slave Stations (campo_aperto type - field sensors)
  project.slaveStations.forEach((slaveCoords, index) => {
    const slaveReadings = generateStationReadings(slaveCoords, false);
    const sectionLetter = String.fromCharCode(65 + index); // A, B, C...
    // Each project should have 4 stations total:
    // - 2 Retro modulo (including the master)
    // - 2 Campo aperto
    // With 1 master + 3 non-master stations, make exactly one of the non-master
    // stations a retro_modulo and keep the other two as campo_aperto.
    const isSecondRetro = index === 2;
    const sensorType = isSecondRetro ? 'retro_modulo' : 'campo_aperto';
    const stationType = sensorType;
    
    stations.push({
      id: `${project.id}-slave-${index + 1}`,
      stationCode: `${baseCode}-${isSecondRetro ? 'RETRO' : 'CAMPO'}-${String(index + 2).padStart(3, '0')}`,
      farmId: project.id,
      farmName: project.title,
      projectId: project.id,
      projectTitle: project.title,
      stationType,
      sensorType,
      isMaster: false,
      locationDescription: `Section ${sectionLetter} - ${isSecondRetro ? 'Retro Modulo' : 'Campo Aperto'}`,
      locationLat: slaveCoords[0],
      locationLon: slaveCoords[1],
      description: isSecondRetro
        ? 'Retro modulo sensor transmitting data via radio to master station'
        : 'Field sensor transmitting data via radio to master station',
      isActive: slaveReadings.isActive,
      status: slaveReadings.status,
      lastTransmission: slaveReadings.lastTransmission,
      signalStrength: slaveReadings.signalStrength,
      batteryLevel: slaveReadings.batteryLevel,
      currentReadings: slaveReadings.currentReadings,
    });
  });

  // Assign stations into two main groups (Section A / Section B), 2 stations each.
  // We choose a split axis/threshold based on the farm polygon and station locations.
  // Then we label each station with locationDescription = "Section A" / "Section B".
  try {
    if (!project.sections?.length && project.polygon && stations.length >= 4) {
      const split = computeFieldSplit(project.polygon, stations);
      if (split) {
        const { axis, threshold } = split;
        const getCoord = (s) => (axis === 'lon' ? s.locationLon : s.locationLat);

        // Sort by axis, assign lowest 2 to A, highest 2 to B (guarantees 2 per group)
        const sorted = [...stations].sort((a, b) => getCoord(a) - getCoord(b));
        const aSet = new Set(sorted.slice(0, 2).map(s => s.id));

        stations.forEach((s) => {
          const section = aSet.has(s.id) ? 'Section A' : 'Section B';
          s.locationDescription = section;
        });
      }
    }
  } catch (e) {
    // If anything goes wrong, keep original locationDescription strings.
  }

  if (project.id === 'project-2') {
    const retroStation = stations.find(
      (station) => station.stationType === 'retro_modulo' && !station.isMaster
    );
    if (retroStation && !retroStation.currentReadings) {
      const generated = generateStationReadings([retroStation.locationLat, retroStation.locationLon], true);
      if (generated.currentReadings) {
        retroStation.currentReadings = {
          ...generated.currentReadings,
          timestamp: retroStation.lastTransmission || generated.currentReadings.timestamp
        };
      }
    }
  }
  
  return stations;
};

// Generate all stations from projects
const MOCK_STATIONS_RAW = PROJECTS_DATA.flatMap(project => generateProjectStations(project));

// Update station statuses based on current conditions
const updateStationStatuses = (stations) => {
  return stations.map(station => {
    const calculatedStatus = calculateStationStatus(station);
    return {
      ...station,
      status: calculatedStatus,
      isActive: calculatedStatus !== 'inactive'
    };
  });
};

// Apply status updates to stations
const STATIONS_WITH_UPDATED_STATUS = updateStationStatuses(MOCK_STATIONS_RAW);

// Calculate stats from the updated data
export const MOCK_STATS = {
  totalStations: STATIONS_WITH_UPDATED_STATUS.length,
  activeStations: STATIONS_WITH_UPDATED_STATUS.filter(s => s.status === 'active').length,
  warningStations: STATIONS_WITH_UPDATED_STATUS.filter(s => s.status === 'warning').length,
  inactiveStations: STATIONS_WITH_UPDATED_STATUS.filter(s => s.status === 'inactive').length,
  stationsNeedingMaintenance: STATIONS_WITH_UPDATED_STATUS.filter(s => s.status === 'warning' || !s.isActive).length,
  masterStations: STATIONS_WITH_UPDATED_STATUS.filter(s => s.isMaster).length,
  slaveStations: STATIONS_WITH_UPDATED_STATUS.filter(s => !s.isMaster).length,
};

// Export stations with updated statuses
export const MOCK_STATIONS = STATIONS_WITH_UPDATED_STATUS;
